/**
 * SceneRouter — coordinator for the five-painted-scene journey.
 *
 * Owns:
 *   - Which painted scene the walker is currently in.
 *   - Interaction flags (e.g. `scene-2-hearth.hearth.lit`) that persist across
 *     reloads so a tended hearth stays tended.
 *   - The walker's last normalised position per scene so back-tracking lands
 *     where she stepped away.
 *   - Persistence to a `Storage` (localStorage by default), namespaced per
 *     save slot so each slot has independent journey state.
 *
 * Does NOT own:
 *   - The Phaser scene instance or rendering — that's `PaintedScene` (AGE-1420).
 *   - The visual transition between scenes — that's `SceneTransition` (AGE-1421).
 *     The router calls a `SceneTransitionRunner` interface and lets that
 *     module handle the dissolve / pan / wash.
 *
 * Phaser-free by design: tests instantiate a router with an in-memory storage
 * and a fake transition runner — no canvas required.
 *
 * See `docs/design/scene-transitions-painted.md` and AGE-1422 spec.
 */

import {
  JourneyManifestSchema,
  type EdgeZone,
  type JourneyManifest,
  type NormalisedPoint,
  type PaintedSceneSpec,
  type TransitionKind,
} from './journeyManifest.schema';
import type { ZonePreloader } from './ZonePreloader';

/** Persisted snapshot of journey state. */
export interface PaintedSceneState {
  /** Manifest schema version this snapshot was written under. */
  version: 1;
  /** The scene the walker is currently in. */
  currentScene: string;
  /** Last normalised walker position in the current scene. */
  walkerPos: NormalisedPoint;
  /** Map of triggered interaction flags. */
  flags: Record<string, true>;
  /**
   * Per-scene last walker position. Restored when the walker re-enters a scene
   * by means *other* than an edge zone (e.g. loading from disk straight back
   * into the current scene).
   */
  lastPositionByScene: Record<string, NormalisedPoint>;
  /** Wall-clock timestamp of the most recent save (ISO 8601). */
  savedAtISO: string;
}

/** Request handed to the transition runner. */
export interface SceneTransitionRequest {
  /** Previous scene key, or null on first entry / fresh load. */
  fromScene: string | null;
  /** Target scene key (always a key from `manifest.scenes[]`). */
  toScene: string;
  /** Which transition style to play. */
  kind: TransitionKind;
  /** Where the walker lands in the target scene (normalised). */
  spawn: NormalisedPoint;
  /** Optional millisecond override for the transition. */
  durationMs?: number;
}

/**
 * Pluggable transition runner. The router calls `run()` and awaits the
 * promise before flipping `currentScene`. Implementations include:
 *   - `PaintedSceneTransitionRunner` — wraps `SceneTransition` (AGE-1421).
 *   - `InstantTransitionRunner` — used in tests; resolves synchronously.
 */
export interface SceneTransitionRunner {
  run(req: SceneTransitionRequest): Promise<void>;
}

/** Trivial runner that resolves immediately — handy for tests and bootstrap. */
export const instantTransitionRunner: SceneTransitionRunner = {
  run() {
    return Promise.resolve();
  },
};

/** Options consumed by a `goTo` call. */
export interface GoToOptions {
  /**
   * Edge zone the walker stepped into. When provided, the router pulls the
   * transition kind, spawn, and duration straight from the manifest's
   * `edgeZones[]` entry — the canonical path for in-scene navigation.
   */
  viaEdgeZoneId?: string;
  /** Override the spawn point in the target scene. */
  spawn?: NormalisedPoint;
  /** Override the transition kind. Defaults to `wash` when nothing else gives one. */
  transitionKind?: TransitionKind;
  /** Override the transition duration in ms. */
  durationMs?: number;
}

export interface SceneRouterOptions {
  /** Pre-validated manifest. Use `loadJourneyManifest()` to fetch + parse. */
  manifest: JourneyManifest;
  /**
   * Storage backend. Defaults to `localStorage` when present; falls back to
   * a no-op in non-browser contexts. Pass an in-memory store for tests.
   */
  storage?: StorageLike;
  /**
   * Namespace key for this router's state in storage. Defaults to
   * `painted_scene_state`. Per-slot routing should pass
   * `painted_scene_state_<slotIndex>` so slots don't trample each other.
   */
  storageKey?: string;
  /**
   * Transition runner. Defaults to `instantTransitionRunner` so the router
   * is usable before SceneTransition lands. Replace with the real runner in
   * `PaintedScene` once AGE-1421 ships.
   */
  transition?: SceneTransitionRunner;
  /**
   * Optional zone preloader for async neighbor pre-loading with LRU eviction.
   * When provided, the router calls `preloadNeighbors()` after every scene enter.
   */
  preloader?: ZonePreloader;
  /**
   * Optional initial state. When omitted, the router tries to load from
   * storage; if that fails, it starts from `manifest.initialScene` with the
   * walker at that scene's `defaultSpawn`.
   */
  initialState?: PaintedSceneState | null;
}

/** Subset of the Web Storage API the router actually uses. */
export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/** Default localStorage namespace when none is supplied. */
export const DEFAULT_SCENE_ROUTER_STORAGE_KEY = 'painted_scene_state';

/** Per-slot namespace builder — keeps slot state isolated. */
export function sceneRouterStorageKeyForSlot(slotIndex: number): string {
  return `${DEFAULT_SCENE_ROUTER_STORAGE_KEY}_${slotIndex}`;
}

/**
 * The `PaintedSceneState` schema is small enough to validate by hand. We avoid
 * a full Zod schema here so router persistence stays cheap and dep-free of the
 * manifest shape (the manifest version is the only versioning we care about).
 */
function isPersistedState(value: unknown): value is PaintedSceneState {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  if (v.version !== 1) return false;
  if (typeof v.currentScene !== 'string') return false;
  if (!isPoint(v.walkerPos)) return false;
  if (!isFlagMap(v.flags)) return false;
  if (!isPositionMap(v.lastPositionByScene)) return false;
  if (typeof v.savedAtISO !== 'string') return false;
  return true;
}

function isPoint(value: unknown): value is NormalisedPoint {
  if (!value || typeof value !== 'object') return false;
  const p = value as Record<string, unknown>;
  return typeof p.x === 'number' && typeof p.y === 'number'
    && p.x >= 0 && p.x <= 1 && p.y >= 0 && p.y <= 1;
}

function isFlagMap(value: unknown): value is Record<string, true> {
  if (!value || typeof value !== 'object') return false;
  for (const v of Object.values(value)) {
    if (v !== true) return false;
  }
  return true;
}

function isPositionMap(value: unknown): value is Record<string, NormalisedPoint> {
  if (!value || typeof value !== 'object') return false;
  for (const v of Object.values(value)) {
    if (!isPoint(v)) return false;
  }
  return true;
}

/**
 * Best-effort resolver for the global `localStorage`. Returns null on
 * Vitest / SSR / private-mode where it's missing or throws on access.
 */
function defaultStorage(): StorageLike | null {
  try {
    if (typeof globalThis !== 'undefined') {
      const ls = (globalThis as unknown as { localStorage?: StorageLike }).localStorage;
      if (ls && typeof ls.getItem === 'function') return ls;
    }
  } catch {
    // ignore — quota / permissions can throw on access
  }
  return null;
}

/**
 * Fetch and validate a journey manifest from a URL. Throws on HTTP failure or
 * schema mismatch — manifests are authored content and a malformed file is a
 * boot-blocking bug, not a runtime fallback.
 */
export async function loadJourneyManifest(
  url: string,
  fetchImpl: typeof fetch = fetch,
): Promise<JourneyManifest> {
  const res = await fetchImpl(url);
  if (!res.ok) {
    throw new Error(`SceneRouter: failed to load journey manifest from ${url} (${res.status})`);
  }
  const raw = await res.json();
  return JourneyManifestSchema.parse(raw);
}

/**
 * Coordinator for the five-scene journey. Holds active scene state, persists
 * it, and delegates the visual handoff between scenes to a transition runner.
 */
export class SceneRouter {
  readonly manifest: JourneyManifest;
  private readonly storage: StorageLike | null;
  private readonly storageKey: string;
  private readonly transition: SceneTransitionRunner;
  private readonly preloader: ZonePreloader | null;

  private state: PaintedSceneState;
  /** Guard against overlapping `goTo` calls firing from rapid input. */
  private inFlight: Promise<void> | null = null;
  private listeners: Array<(state: PaintedSceneState) => void> = [];

  constructor(opts: SceneRouterOptions) {
    this.manifest = opts.manifest;
    this.storage = opts.storage ?? defaultStorage();
    this.storageKey = opts.storageKey ?? DEFAULT_SCENE_ROUTER_STORAGE_KEY;
    this.transition = opts.transition ?? instantTransitionRunner;
    this.preloader = opts.preloader ?? null;

    const loaded = opts.initialState !== undefined
      ? opts.initialState
      : this.loadPersisted();

    if (loaded && this.stateMatchesManifest(loaded)) {
      this.state = loaded;
    } else {
      // First boot for this storage key, or persisted state went stale —
      // start fresh and write the new baseline so a crash before the first
      // navigation still leaves a recoverable snapshot on disk.
      this.state = this.freshState();
      this.persist();
    }
  }

  // ── state accessors ────────────────────────────────────────────────────

  /** Snapshot of current state. Callers must treat this as read-only. */
  getState(): Readonly<PaintedSceneState> {
    return this.state;
  }

  currentSceneKey(): string {
    return this.state.currentScene;
  }

  currentSceneSpec(): PaintedSceneSpec {
    const spec = this.getSceneSpec(this.state.currentScene);
    if (!spec) {
      throw new Error(`SceneRouter: current scene "${this.state.currentScene}" missing from manifest`);
    }
    return spec;
  }

  getSceneSpec(key: string): PaintedSceneSpec | undefined {
    return this.manifest.scenes.find(s => s.key === key);
  }

  // ── interaction flags ──────────────────────────────────────────────────

  hasFlag(flag: string): boolean {
    return this.state.flags[flag] === true;
  }

  /** Mark an interaction flag as triggered. No-op if already set. */
  markFlag(flag: string): void {
    if (this.state.flags[flag]) return;
    this.state = {
      ...this.state,
      flags: { ...this.state.flags, [flag]: true },
    };
    this.persist();
  }

  /** Clear a single flag — used by debug tooling and tests. */
  clearFlag(flag: string): void {
    if (!this.state.flags[flag]) return;
    const { [flag]: _, ...rest } = this.state.flags;
    void _;
    this.state = { ...this.state, flags: rest };
    this.persist();
  }

  // ── walker position ────────────────────────────────────────────────────

  /**
   * Update the walker's normalised position in the current scene. Coordinates
   * are clamped to [0, 1] so a stray off-canvas value can't poison persistence.
   */
  setWalkerPosition(p: NormalisedPoint): void {
    const clamped = clampPoint(p);
    this.state = {
      ...this.state,
      walkerPos: clamped,
      lastPositionByScene: {
        ...this.state.lastPositionByScene,
        [this.state.currentScene]: clamped,
      },
    };
    this.persist();
  }

  // ── navigation ─────────────────────────────────────────────────────────

  /**
   * Navigate to a target scene. Resolves the transition kind, spawn, and
   * duration in priority order:
   *   1. explicit overrides on `opts`
   *   2. matching `viaEdgeZoneId` on the *current* scene's `edgeZones[]`
   *   3. target scene's `defaultSpawn` + `wash` fallback transition
   *
   * If a `goTo` is already in flight, returns the in-flight promise so rapid
   * key-mash doesn't double-fire.
   */
  goTo(target: string, opts: GoToOptions = {}): Promise<void> {
    if (this.inFlight) return this.inFlight;
    const promise = this.runGoTo(target, opts).finally(() => {
      this.inFlight = null;
    });
    this.inFlight = promise;
    return promise;
  }

  /**
   * Convenience entry point for edge-zone hits. Looks up the edge zone on the
   * current scene, then dispatches `goTo` with the manifest values.
   */
  goToViaEdgeZone(zoneId: string): Promise<void> {
    const zone = this.findEdgeZoneOnCurrentScene(zoneId);
    if (!zone) {
      return Promise.reject(new Error(
        `SceneRouter: no edge zone "${zoneId}" on scene "${this.state.currentScene}"`,
      ));
    }
    return this.goTo(zone.targetScene, { viaEdgeZoneId: zoneId });
  }

  private async runGoTo(target: string, opts: GoToOptions): Promise<void> {
    const targetSpec = this.getSceneSpec(target);
    if (!targetSpec) {
      throw new Error(`SceneRouter: unknown target scene "${target}"`);
    }

    const fromScene = this.state.currentScene;
    const edgeZone = opts.viaEdgeZoneId
      ? this.findEdgeZoneOnCurrentScene(opts.viaEdgeZoneId)
      : null;
    if (opts.viaEdgeZoneId && !edgeZone) {
      throw new Error(
        `SceneRouter: edge zone "${opts.viaEdgeZoneId}" not found on scene "${fromScene}"`,
      );
    }
    if (edgeZone && edgeZone.targetScene !== target) {
      throw new Error(
        `SceneRouter: edge zone "${opts.viaEdgeZoneId}" targets "${edgeZone.targetScene}", not "${target}"`,
      );
    }

    const spawn = opts.spawn
      ?? edgeZone?.spawn
      ?? this.state.lastPositionByScene[target]
      ?? targetSpec.defaultSpawn;

    const kind: TransitionKind = opts.transitionKind
      ?? edgeZone?.transitionKind
      ?? 'wash';

    const durationMs = opts.durationMs ?? edgeZone?.durationMs;

    await this.transition.run({
      fromScene,
      toScene: target,
      kind,
      spawn,
      ...(durationMs !== undefined ? { durationMs } : {}),
    });

    this.state = {
      ...this.state,
      currentScene: target,
      walkerPos: spawn,
      lastPositionByScene: {
        ...this.state.lastPositionByScene,
        [target]: spawn,
      },
    };
    this.persist();

    if (this.preloader) {
      void this.preloader.preloadNeighbors(target);
    }
  }

  private findEdgeZoneOnCurrentScene(zoneId: string): EdgeZone | undefined {
    return this.currentSceneSpec().edgeZones.find(z => z.id === zoneId);
  }

  // ── persistence ────────────────────────────────────────────────────────

  /** Write current state to storage. Silently swallows storage errors. */
  persist(): void {
    this.state = { ...this.state, savedAtISO: new Date().toISOString() };
    this.notify();
    if (!this.storage) return;
    try {
      this.storage.setItem(this.storageKey, JSON.stringify(this.state));
    } catch {
      // localStorage unavailable / quota — best-effort
    }
  }

  /** Wipe persisted state and reset to manifest defaults. */
  reset(): void {
    this.state = this.freshState();
    if (this.storage) {
      try {
        this.storage.removeItem(this.storageKey);
      } catch {
        // best-effort
      }
    }
    this.persist();
  }

  /** Subscribe to state changes. Returns an unsubscribe function. */
  onChange(listener: (state: PaintedSceneState) => void): () => void {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  private notify(): void {
    for (const listener of this.listeners) {
      try {
        listener(this.state);
      } catch {
        // listeners must not throw — swallow so persistence doesn't break
      }
    }
  }

  private loadPersisted(): PaintedSceneState | null {
    if (!this.storage) return null;
    try {
      const raw = this.storage.getItem(this.storageKey);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as unknown;
      if (!isPersistedState(parsed)) return null;
      return parsed;
    } catch {
      return null;
    }
  }

  private stateMatchesManifest(state: PaintedSceneState): boolean {
    return Boolean(this.getSceneSpec(state.currentScene));
  }

  private freshState(): PaintedSceneState {
    const initial = this.getSceneSpec(this.manifest.initialScene);
    if (!initial) {
      throw new Error(
        `SceneRouter: manifest.initialScene "${this.manifest.initialScene}" not found in scenes[]`,
      );
    }
    return {
      version: 1,
      currentScene: initial.key,
      walkerPos: { ...initial.defaultSpawn },
      flags: {},
      lastPositionByScene: { [initial.key]: { ...initial.defaultSpawn } },
      savedAtISO: new Date().toISOString(),
    };
  }
}

function clampPoint(p: NormalisedPoint): NormalisedPoint {
  return {
    x: Math.max(0, Math.min(1, p.x)),
    y: Math.max(0, Math.min(1, p.y)),
  };
}
